import * as React from "react";
import {
  markerStyle,
  clusterMarkerStyle,
  specEqual,
  type MarkerSpec,
  type MarkerStyle,
} from "./markers";
import { clusterByGrid } from "./clustering";
import { hideMarkerTooltip, attachMarkerTooltip, bindMapTooltipHide } from "./tooltip";
import type { EntityKind } from "./types";

// 가시성 맵 한 항목. map.tsx의 useMemo 결과 형태와 동일.
export interface Visibility {
  rendered: boolean;
  dim: boolean;
}

export interface Selected {
  kind: EntityKind;
  id: number;
}

interface UseNaverMarkersArgs {
  map: any | null;
  data: any | null;
  orgVisibility: Map<number, Visibility>;
  elderlyVisibility: Map<number, Visibility>;
  managerVisibility: Map<number, Visibility>;
  selected: Selected | null;
  onSelect: (sel: Selected) => void;

  // 그리드 클러스터링 활성. 기본 false.
  // ON일 때 종별로 cellPx 그리드에 묶고, 묶인 셀은 클러스터 마커로 표시한다.
  // 사용자가 줌인하면 셀 크기가 작아져 자연스레 분해됨.
  clustering?: boolean;

  // 클러스터 그리드 크기(px). 기본 80. 작을수록 더 잘게 묶임.
  clusterCellPx?: number;
}

// 마커 ref가 들고 있을 상태. count=1이면 개별 마커, >1이면 클러스터.
interface AttachedEntry {
  marker: any;
  // 개별 vs 클러스터를 구분 — setIcon 갱신 분기에 사용.
  count: number;
  // 개별 마커일 때만 의미 있는 spec(dim/selected) 비교용.
  spec: MarkerSpec;
}

// 라이프사이클 정책:
//   - SDK ready + map 인스턴스 준비된 후에만 마커를 만진다.
//   - clustering OFF: 단순히 항목당 마커 1개.
//   - clustering ON:  종별로 cellPx 그리드 묶음 → count=1 single 또는 count>1 cluster.
//   - 매 렌더에서 desired를 계산해 현재 마커와 diff. 제거/추가/setIcon 갱신만.
//   - clustering ON일 때 map idle 이벤트(디바운스 150ms)로 viewVersion을 트리거해
//     줌/팬 변화에 따라 클러스터를 재계산한다.
export function useNaverMarkers({
  map,
  data,
  orgVisibility,
  elderlyVisibility,
  managerVisibility,
  selected,
  onSelect,
  clustering = false,
  clusterCellPx = 80,
}: UseNaverMarkersArgs): void {
  const markersRef = React.useRef(new Map<string, AttachedEntry>());

  // onSelect는 매 렌더마다 새 함수일 수 있으므로 ref로 안정화.
  const onSelectRef = React.useRef(onSelect);
  React.useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // 줌/팬 변화에 따른 재클러스터링 트리거. clustering ON일 때만 idle 리스너 부착.
  const [viewVersion, setViewVersion] = React.useState(0);
  React.useEffect(() => {
    if (!map || !window.naver?.maps || !clustering) return;
    const naver = window.naver;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const listener = naver.maps.Event.addListener(map, "idle", () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => setViewVersion((v) => v + 1), 150);
    });
    return () => {
      try { naver.maps.Event.removeListener(listener); } catch {}
      if (debounce) clearTimeout(debounce);
    };
  }, [map, clustering]);

  // 지도 조작 중에는 툴팁을 닫는다(앵커가 어긋나므로).
  React.useEffect(() => {
    if (!map || !window.naver?.maps) return;
    return bindMapTooltipHide(window.naver, map);
  }, [map]);

  React.useEffect(() => {
    if (!map || !window.naver?.maps || !data) return;
    const naver = window.naver;

    // 1) desired 계산. (kind, count, items, spec) 셋업.
    const desired = new Map<string, DesiredEntry>();

    const processKind = (
      kind: EntityKind,
      list: any[] | undefined,
      vis: Map<number, Visibility>,
    ) => {
      const visible: any[] = [];
      for (const it of list ?? []) {
        const v = vis.get(it.id);
        if (!v?.rendered) continue;
        if (it.lat == null || it.lng == null) continue;
        visible.push(it);
      }
      if (visible.length === 0) return;

      if (clustering) {
        const groups = clusterByGrid(visible, map, clusterCellPx, kind);
        for (const g of groups) {
          if (g.items.length === 1) {
            const it = g.items[0];
            const v = vis.get(it.id)!;
            const isSel = selected?.kind === kind && selected.id === it.id;
            // single 키 = "kind:single:id" → clustering OFF와 같은 키 → 마커 재사용 가능.
            desired.set(g.key, { kind, isCluster: false, item: it, spec: { dim: v.dim, selected: isSel } });
          } else {
            // 클러스터 마커. dim/selected는 의미 없음(unit이 아니므로).
            desired.set(g.key, { kind, isCluster: true, count: g.items.length, lat: g.lat, lng: g.lng });
          }
        }
      } else {
        for (const it of visible) {
          const v = vis.get(it.id)!;
          const isSel = selected?.kind === kind && selected.id === it.id;
          // clustering OFF에서도 "kind:single:id" 키를 사용해 토글 시 마커가 재사용되게.
          desired.set(`${kind}:single:${it.id}`, {
            kind, isCluster: false, item: it, spec: { dim: v.dim, selected: isSel },
          });
        }
      }
    };

    processKind("org",     data.organizations, orgVisibility);
    processKind("elderly", data.elderlies,     elderlyVisibility);
    processKind("manager", data.care_managers, managerVisibility);

    const current = markersRef.current;

    // 2) 제거: desired에 없는 키.
    for (const [key, entry] of current) {
      if (!desired.has(key)) {
        try { entry.marker.setMap(null); } catch {}
        current.delete(key);
      }
    }

    // 3) 추가/업데이트.
    for (const [key, d] of desired) {
      const existing = current.get(key);

      if (!d.isCluster) {
        // 개별 마커.
        const style = markerStyle(d.kind, d.item, d.spec);
        if (!existing || existing.count !== 1) {
          // 새로 만들거나, 같은 키가 직전엔 클러스터였던 경우(드물지만 가능) → 교체.
          if (existing) { try { existing.marker.setMap(null); } catch {} }
          const item = d.item;
          const kind = d.kind;
          const marker = createMarkerObject(naver, map, item.lat, item.lng, style, () => {
            onSelectRef.current({ kind, id: item.id });
          });
          current.set(key, { marker, count: 1, spec: d.spec });
        } else if (!specEqual(existing.spec, d.spec)) {
          // spec(dim/selected)만 바뀐 경우 — setIcon으로 content/anchor/z 갱신.
          applyStyle(naver, existing.marker, style);
          existing.spec = d.spec;
        }
      } else {
        // 클러스터 마커.
        const style = clusterMarkerStyle(d.kind, d.count);
        const lat = d.lat;
        const lng = d.lng;
        if (!existing || existing.count !== d.count) {
          // 처음 만들거나, count가 바뀐 경우 — 같은 셀이라도 항목 추가/제거로 크기가 변함.
          // 같은 key + count 변화는 다음 두 가지: ① 셀 안 항목 수가 실제로 바뀜
          // ② 줌이 바뀌어 셀 자체가 다른 의미. 둘 다 setIcon으로 갱신해 같은 마커
          // 객체를 유지한다 (위치 평균은 cluster diff 마다 갱신).
          if (existing) {
            try { existing.marker.setPosition(new naver.maps.LatLng(lat, lng)); } catch {}
            applyStyle(naver, existing.marker, style);
            existing.count = d.count;
          } else {
            const marker = createMarkerObject(naver, map, lat, lng, style, () => {
              zoomToCluster(naver, map, lat, lng);
            });
            current.set(key, { marker, count: d.count, spec: {} });
          }
        } else {
          // 같은 count여도 좌표 평균은 항목 변동으로 미세 변화 가능 — 위치만 갱신.
          try { existing.marker.setPosition(new naver.maps.LatLng(lat, lng)); } catch {}
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, data, orgVisibility, elderlyVisibility, managerVisibility, selected, clustering, clusterCellPx, viewVersion]);

  // 언마운트 정리.
  React.useEffect(() => {
    return () => {
      for (const [, { marker }] of markersRef.current) {
        try { marker.setMap(null); } catch {}
      }
      markersRef.current.clear();
      hideMarkerTooltip();
    };
  }, []);
}

// ── helpers ────────────────────────────────────────────────────────

// desired map 값의 합집합 타입.
// isCluster 태그로 narrow — count 비교만으로는 TS가 union을 좁히지 못한다.
type DesiredEntry =
  | { kind: EntityKind; isCluster: false; item: any; spec: MarkerSpec }
  | { kind: EntityKind; isCluster: true;  count: number; lat: number; lng: number };

function createMarkerObject(
  naver: any,
  map: any,
  lat: number,
  lng: number,
  style: MarkerStyle,
  onClick: () => void,
): any {
  const marker = new naver.maps.Marker({
    map,
    position: new naver.maps.LatLng(lat, lng),
    icon: {
      content: style.content,
      anchor: new naver.maps.Point(style.anchorX, style.anchorY),
    },
    zIndex: style.zIndex,
    // 네이티브 title은 주지 않는다 — 아래 커스텀 툴팁과 두 겹으로 뜬다.
  });
  attachMarkerTooltip(naver, marker, style.tooltip);
  naver.maps.Event.addListener(marker, "click", onClick);
  return marker;
}

function applyStyle(naver: any, marker: any, style: MarkerStyle): void {
  try {
    marker.setIcon({
      content: style.content,
      anchor: new naver.maps.Point(style.anchorX, style.anchorY),
    });
    marker.setZIndex(style.zIndex);
    marker.__tooltip = style.tooltip;
  } catch {}
}

// 클러스터 클릭 → 두 단계 줌인 + 클릭 좌표로 panTo. 같은 좌표가 다수 겹친
// 케이스는 줌인해도 분해 안 되지만, 그 정도는 spiderfy(향후)에서 처리.
function zoomToCluster(naver: any, map: any, lat: number, lng: number): void {
  const position = new naver.maps.LatLng(lat, lng);
  const currentZoom = typeof map.getZoom === "function" ? map.getZoom() : 12;
  try {
    map.setCenter(position);
    map.setZoom(Math.min(18, currentZoom + 2), true);
  } catch {
    try { map.panTo(position); } catch {}
  }
}
